import RecordRTC from 'recordrtc';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/axios';
import { getFilterTabs, applyInboxFilter, getInboxCounts, getGsmTag } from '../../utils/inboxFilters';

const getDateLabel = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((today - msgDay) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString([], { day:'2-digit', month:'long', year:'numeric' });
};

const avatarColors = ['#FF6B6B','#4ECDC4','#45B7D1','#96CEB4','#DDA0DD','#98D8C8','#F7DC6F','#BB8FCE','#85C1E9','#F0A500'];
const getColor = (name) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];
const getInitials = (name) => { if (!name) return '?'; return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2); };
const formatTime = (ts) => { if (!ts) return ''; const d = new Date(ts); const now = new Date(); if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); const y = new Date(now); y.setDate(now.getDate() - 1); if (d.toDateString() === y.toDateString()) return 'Yesterday'; return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' }); };
const get24HrWindow = (ts) => { if (!ts) return null; const diff = new Date(new Date(ts).getTime() + 86400000) - new Date(); if (diff <= 0) return null; return Math.floor(diff/3600000) + 'h ' + Math.floor((diff%3600000)/60000) + 'm'; };
const labelColors = { green: { bg: '#dcfce7', color: '#166534' }, yellow: { bg: '#fef9c3', color: '#854d0e' }, red: { bg: '#fee2e2', color: '#991b1b' }, blue: { bg: '#e0f2fe', color: '#075985' }, purple: { bg: '#f3e8ff', color: '#6b21a8' }, orange: { bg: '#ffedd5', color: '#9a3412' } };

export default function MobileInbox() {
  const [searchParams] = useSearchParams();
  const isApp = searchParams.get('app') === 'true';
  const { user, loading, selectedBusiness, availableBusinesses, selectBusiness } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [fetching, setFetching] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState('');
  const [newChatTemplate, setNewChatTemplate] = useState('');
  const [newChatVars, setNewChatVars] = useState({});
  const [labels, setLabels] = useState([]);
  const [contactLabels, setContactLabels] = useState([]);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showSaveContact, setShowSaveContact] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showBizSwitcher, setShowBizSwitcher] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [cancelSlide, setCancelSlide] = useState(false);
  const micStartX = useRef(null);
  const micStartY = useRef(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingStartRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const [saveContactName, setSaveContactName] = useState('');
  const [notes, setNotes] = useState([]);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [msgSearch, setMsgSearch] = useState('');
  const [showMsgSearch, setShowMsgSearch] = useState(false);
  const [lastMsgCount, setLastMsgCount] = useState(0);
  const [copiedMsg, setCopiedMsg] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [showManageLabels, setShowManageLabels] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('green');
  const [labelFilter, setLabelFilter] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [showManageQR, setShowManageQR] = useState(false);
  const [newQRMessage, setNewQRMessage] = useState('');
  const chatEndRef = useRef(null);
  const bizId = selectedBusiness?.id || selectedBusiness?.business_id;
  console.log('MOBILE DEBUG bizId:', bizId, 'selectedBusiness:', JSON.stringify(selectedBusiness));
  const tagConfig = (() => { try { return selectedBusiness?.tag_config ? JSON.parse(selectedBusiness.tag_config) : null; } catch (e) { return null; } })();

  useEffect(() => { if (!loading && !user) navigate('/mobile-login?app=true'); }, [user, loading, navigate]);

  const fetchConversations = useCallback(async () => {
    if (!bizId) return;
    try { const { data } = await api.get('/messages/conversations?business_id=' + bizId); setConversations(Array.isArray(data) ? data : data.conversations || []); } catch {} finally { setFetching(false); }
  }, [bizId]);

  const fetchLabels = useCallback(async () => {
    if (!bizId) return;
    try { const { data } = await api.get('/messages/labels?business_id=' + bizId); setLabels(data || []); } catch {}
  }, [bizId]);

  const fetchQuickReplies = useCallback(async () => {
    if (!bizId) return;
    try { const { data } = await api.get('/messages/quick-replies?business_id=' + bizId); setQuickReplies(data || []); } catch {}
  }, [bizId]);

  useEffect(() => {
    if (user && bizId) {
      fetchConversations();
      fetchLabels();
      fetchQuickReplies();
      // Auto save FCM token when user logs in
      if (window.saveFCMToken) {
        import('firebase/app').catch(() => {});
      }
      // Try to get token from Android app
      if (window._fcmToken || window._pendingFcmToken) {
        api.post('/notifications/save-device-token', {
          user_id: user.id,
          business_id: bizId,
          token: window._fcmToken || window._pendingFcmToken
        }).catch(() => {});
      }
      const poll = setInterval(fetchConversations, 8000);
      return () => clearInterval(poll);
    }
  }, [user, bizId, fetchConversations, fetchLabels, fetchQuickReplies]);

  useEffect(() => {
    if (bizId) api.get('/templates?business_id=' + bizId).then(r => setTemplates(r.data || [])).catch(() => {});
  }, [bizId]);

  const fetchMessages = useCallback(async (phone, scrollToBottom = true) => {
    if (!phone || !bizId) return;
    try {
      const { data } = await api.get('/messages/chat?business_id=' + bizId + '&phone=' + phone);
      const withMedia = await Promise.all((data || []).map(async msg => {
        if (msg.media_id) { try { const r = await api.get('/messages/media-url?media_id=' + msg.media_id + '&business_id=' + bizId); return { ...msg, media_url: r.data.url }; } catch { return msg; } }
        return msg;
      }));
      setMessages(withMedia);

    } catch { setMessages([]); }
  }, [bizId]);

  const fetchContactLabels = useCallback(async (contactId) => {
    if (!contactId || !bizId) return;
    try { const { data } = await api.get('/messages/contact-labels?business_id=' + bizId + '&contact_id=' + contactId); setContactLabels(data || []); } catch {}
  }, [bizId]);

  const fetchNotes = async (contactId) => {
    if (!contactId || !bizId) return;
    try { const { data } = await api.get('/messages/contact-notes?business_id=' + bizId + '&contact_id=' + contactId); setNotes(data || []); } catch {}
  };

  const addNote = async () => {
    if (!newNote.trim() || !selected) return;
    try { await api.post('/messages/contact-notes', { business_id: bizId, contact_id: selected.contact_id, note: newNote.trim() }); setNewNote(''); await fetchNotes(selected.contact_id); }
    catch (e) { alert('Failed: ' + e.message); }
  };

  const deleteNote = async (id) => {
    try { await api.delete('/messages/contact-notes/' + id); await fetchNotes(selected.contact_id); }
    catch (e) { alert('Failed: ' + e.message); }
  };

  const playSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.3, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      o.start(ctx.currentTime);
      o.stop(ctx.currentTime + 0.3);
    } catch {}
  };

  const copyMessage = (msg) => {
    navigator.clipboard.writeText(msg).then(() => {
      setCopiedMsg(msg);
      setTimeout(() => setCopiedMsg(null), 2000);
    }).catch(() => {});
  };

  const openConversation = async (conv) => {
    setSelected(conv);
    fetchMessages(conv.phone);
    fetchContactLabels(conv.contact_id);
    fetchNotes(conv.contact_id);
    api.post('/messages/mark-read', { business_id: bizId, phone: conv.phone }).catch(() => {});
    setConversations(prev => prev.map(c => c.phone === conv.phone ? { ...c, unread_count: 0 } : c));
  };

  useEffect(() => {
    if (selected) { const poll = setInterval(async () => {
      if (!selected?.phone || !bizId) return;
      try {
        const { data } = await api.get('/messages/chat?business_id=' + bizId + '&phone=' + selected.phone);
        const newCount = (data || []).length;
        if (newCount > lastMsgCount && lastMsgCount > 0) playSound();
        setLastMsgCount(newCount);
        const withMedia = await Promise.all((data || []).map(async msg => {
          if (msg.media_id) { try { const r = await api.get('/messages/media-url?media_id=' + msg.media_id + '&business_id=' + bizId); return { ...msg, media_url: r.data.url }; } catch { return msg; } }
          return msg;
        }));
        setMessages(withMedia);
      } catch {}
    }, 5000); return () => clearInterval(poll); }
  }, [selected, fetchMessages]);

  useEffect(() => {
    const container = document.getElementById('chat-scroll');
    if (!container || !chatEndRef.current) return;
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
    if (nearBottom) { chatEndRef.current.scrollIntoView({ behavior: "auto" }); }
  }, [messages, selected]);
  useEffect(() => {
    window.saveFCMToken = async (token) => {
      try {
        await api.post('/notifications/save-device-token', {
          user_id: user?.id,
          business_id: bizId,
          token: token
        });
        console.log('FCM token saved:', token);
      } catch (error) {
        console.log('Error saving token:', error);
      }
    };
  }, [user, bizId]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new RecordRTC(stream, {
        type: 'audio',
        mimeType: 'audio/webm',
        recorderType: RecordRTC.MediaStreamRecorder,
        numberOfAudioChannels: 1,
        desiredSampRate: 16000,
      });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorderRef._stream = stream;
      mediaRecorder.startRecording();
      setIsRecording(true); setIsLocked(false); setCancelSlide(false);
      setRecordingTime(0);
      recordingStartRef.current = Date.now();
      recordingTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordingStartRef.current) / 1000);
        setRecordingTime(elapsed);
      }, 500);
    } catch(e) {
      console.log('Mic error:', e.name, e.message);
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        alert('Please allow microphone access:\n\nSettings > Site Settings > Microphone > Allow');
      } else if (e.name === 'NotFoundError') {
        alert('No microphone found on this device');
      } else {
        alert('Microphone error: ' + e.message);
      }
    }
  };
  const stopRecording = (cancel = false) => {
    if(mediaRecorderRef.current && isRecording) {
      mediaRecorderRef._cancelled = cancel;
      const streamToStop = mediaRecorderRef._stream;
      mediaRecorderRef.current.stopRecording(async () => {
        if (!mediaRecorderRef._cancelled) {
          const audioBlob = mediaRecorderRef.current.getBlob();
          const audioFile = new File([audioBlob], 'voice_note.webm', { type: 'audio/webm' });
          await uploadMedia(audioFile);
        }
        if (streamToStop) streamToStop.getTracks().forEach(t => t.stop());
        setIsRecording(false); setIsLocked(false); setCancelSlide(false);
        setRecordingTime(0); clearInterval(recordingTimerRef.current);
        mediaRecorderRef._cancelled = false;
      });
    }
  };
  const onMicTouchStart = (e) => {
    micStartX.current = e.touches ? e.touches[0].clientX : e.clientX;
    micStartY.current = e.touches ? e.touches[0].clientY : e.clientY;
    startRecording();
  };
  const onMicTouchMove = (e) => {
    if (!isRecording) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = x - micStartX.current;
    const dy = y - micStartY.current;
    if (dx < -60) setCancelSlide(true);
    if (dy < -60) { setIsLocked(true); }
  };
  const onMicTouchEnd = () => {
    if (isLocked) return;
    if (cancelSlide) { stopRecording(true); } 
    else { stopRecording(false); }
  };
  const uploadMedia = async (file) => {
    if (!file || !selected) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('business_id', bizId);
    try {
      const { data } = await api.post('/messages/upload-media', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const mediaType = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'document';
      await api.post('/messages/send-media', { business_id: bizId, phone: selected.phone, media_url: data.media_url, media_id: data.media_id, media_type: mediaType, filename: file.name });
      await fetchMessages(selected.phone);
    } catch (e) { alert('Upload failed: ' + (e.response?.data?.error || e.message)); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected || sending) return;
    setSending(true);
    const msgText = replyTo ? `↩️ ${replyTo.message}\n\n${reply}` : reply;
    try { await api.post('/messages/send-text', { business_id: bizId, phone: selected.phone, message: msgText }); setReply(''); setReplyTo(null); await fetchMessages(selected.phone); }
    catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
    finally { setSending(false); }
  };

  const sendTemplate = async (templateName) => {
    if (!selected) return;
    try { await api.post('/messages/send', { business_id: bizId, phone: selected.phone, template_name: templateName, language: 'en_US' }); setShowTemplateModal(false); await fetchMessages(selected.phone); }
    catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const sendNewChat = async () => {
    if (!newChatPhone.trim() || !newChatTemplate) return;
    const cleanPhone = newChatPhone.replace(/[^0-9]/g, '');
    try {
      await api.post('/contacts', { phone: cleanPhone, name: cleanPhone, business_id: bizId }).catch(() => {});
      const tpl = templates.find(t => t.name === newChatTemplate);
      const matches = tpl ? [...(tpl.body || '').matchAll(/\{\{(\d+)\}\}/g)] : [];
      const varParams = matches.map(m => ({ type: 'text', text: newChatVars[m[1]] || '' }));
      await api.post('/messages/send', { business_id: bizId, phone: cleanPhone, template_name: newChatTemplate, language: 'en_US', components: varParams.length ? [{ type: 'body', parameters: varParams }] : undefined });
      setShowNewChat(false); setNewChatPhone(''); setNewChatTemplate(''); setNewChatVars({});
      await fetchConversations();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const toggleLabel = async (label) => {
    if (!selected) return;
    const has = contactLabels.find(l => l.id === label.id);
    try {
      if (has) { await api.delete('/messages/contact-labels', { data: { contact_id: selected.contact_id, label_id: label.id } }); setContactLabels(prev => prev.filter(l => l.id !== label.id)); }
      else { await api.post('/messages/contact-labels', { business_id: bizId, contact_id: selected.contact_id, label_id: label.id }); setContactLabels(prev => [...prev, label]); }
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const createLabel = async () => {
    if (!newLabelName.trim()) return;
    try { await api.post('/messages/labels', { business_id: bizId, name: newLabelName.trim(), color: newLabelColor }); setNewLabelName(''); await fetchLabels(); }
    catch (e) { alert('Failed: ' + e.message); }
  };

  const deleteLabel = async (id) => {
    try { await api.delete('/messages/labels/' + id); await fetchLabels(); }
    catch (e) { alert('Failed: ' + e.message); }
  };

  const addQuickReply = async () => {
    if (!newQRMessage.trim()) return;
    try { await api.post('/messages/quick-replies', { business_id: bizId, message: newQRMessage.trim() }); setNewQRMessage(''); await fetchQuickReplies(); }
    catch (e) { alert('Failed: ' + e.message); }
  };

  const deleteQuickReply = async (id) => {
    try { await api.delete('/messages/quick-replies/' + id); await fetchQuickReplies(); }
    catch (e) { alert('Failed: ' + e.message); }
  };

  const applyQuickReply = (msg) => { setReply(msg); setShowQuickReplies(false); };

  const counts = {
    ...getInboxCounts(conversations, tagConfig, bizId),
    unanswered: conversations.filter(c => c.last_direction === 'inbound').length,
  };

  const filtered = (() => {
    const searched = conversations.filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.phone?.includes(search));
    let base = searched;
    if (filter === 'unanswered') base = searched.filter(c => c.last_direction === 'inbound');
    else if (filter !== 'all') base = applyInboxFilter(searched, filter);
    if (labelFilter) {
      return base.filter(c => c.label_ids && c.label_ids.split(',').map(Number).includes(labelFilter.id));
    }
    return base;
  })();
  const gsmTagLabels = {
    jobcard_ready: { bg: '#dcfce7', color: '#166534', text: '✅ Ready' },
    jobcard_closed: { bg: '#e5e7eb', color: '#374151', text: '🔒 Closed' },
    jobcard_assigned: { bg: '#dbeafe', color: '#1d4ed8', text: '👷 Assigned' },
    service_request: { bg: '#fef9c3', color: '#854d0e', text: '🛠️ Request' },
    service_request_warranty: { bg: '#fce7f3', color: '#9d174d', text: '🛡️ Warranty' },
    server_alert: { bg: '#fee2e2', color: '#991b1b', text: '🚨 Alert' },
    cashback: { bg: '#fef3c7', color: '#92400e', text: '💰 Cashback' },
    location: { bg: '#dbeafe', color: '#1d4ed8', text: '📍 Location' },
    tracking: { bg: '#dbeafe', color: '#1d4ed8', text: '📦 Tracking' },
    custom: { bg: '#f3e8ff', color: '#6b21a8', text: 'Custom' },
  };


  const getTypeLabel = (conv) => {
    if (conv.last_msg_type === 'shopify') return { bg: '#dcfce7', color: '#166534', text: 'Order' };
    if (conv.has_cart == 1) return { bg: '#fef9c3', color: '#854d0e', text: 'Cart' };
    if (conv.last_msg_type === 'template' || conv.last_msg_type === 'invoice') { const gt = getGsmTag(conv); return gsmTagLabels[gt] || { bg: '#f3e8ff', color: '#6b21a8', text: 'Invoice' }; }
    if (conv.last_msg_type === 'Tracking') return { bg: '#dbeafe', color: '#1d4ed8', text: '📦 Tracking' };
    if (conv.last_msg_type === 'shipped' || conv.last_msg_type === 'shopify_fulfillment') return { bg: '#ffedd5', color: '#9a3412', text: '🚚 Shipped' };
    if (conv.label_ids) {
      const firstLabelId = Number(conv.label_ids.split(',')[0]);
      const matchedLabel = labels.find(l => l.id === firstLabelId);
      if (matchedLabel) {
        const lc = labelColors[matchedLabel.color] || labelColors.green;
        return { bg: lc.bg, color: lc.color, text: matchedLabel.name };
      }
    }
    if (conv.has_inbound === 1 || conv.has_inbound === true) return { bg: '#e0f2fe', color: '#075985', text: 'Inquiry' };
    return null;
  };

  // isApp check removed

  const filterTabs = [
    { key:'unanswered', label:'⏳ Pending' },
    ...getFilterTabs(bizId, conversations, tagConfig).map(t => ({ key: t.id, label: t.label })),
  ];

  return (
    <div style={{ minHeight:'100vh', background:'#f0f2f5', fontFamily:'DM Sans, sans-serif', maxWidth:'100%', width:'100%', overflowX:'hidden', boxSizing:'border-box', margin:'0 auto' }}>

      {/* Header */}
      <div style={{ background:'#25D366', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100, boxShadow:'0 2px 8px rgba(0,0,0,0.15)' }}>
        {selected ? (
          <>
            <button onClick={() => { setSelected(null); setMessages([]); setContactLabels([]); }} style={{ background:'none', border:'none', color:'#fff', fontSize:22, cursor:'pointer', padding:0 }}>←</button>
            <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, marginLeft:8 }}>
              <div style={{ width:36, height:36, borderRadius:'50%', background:getColor(selected.name || selected.phone), display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:13, flexShrink:0 }}>{getInitials(selected.name || selected.phone)}</div>
              <div>
                <div style={{ color:'#fff', fontWeight:700, fontSize:15 }}>{selected.name || selected.phone}</div>
                {(() => { const w = get24HrWindow(selected.last_message_time); return w ? <div style={{ color:'rgba(255,255,255,0.85)', fontSize:11 }}>⏱ {w} left</div> : <div style={{ color:'rgba(255,255,255,0.7)', fontSize:11 }}>Window closed</div>; })()}
              </div>
            </div>
            <div style={{ display:'flex', gap:6 }}>
              <button onClick={() => setShowLabelModal(true)} style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', padding:'6px 8px', borderRadius:8, cursor:'pointer', fontSize:12 }}>🏷️</button>
              <button onClick={() => setShowNotesModal(true)} style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', padding:'6px 8px', borderRadius:8, cursor:'pointer', fontSize:12 }}>📝</button>
              <button onClick={() => setShowTemplateModal(true)} style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', padding:'6px 8px', borderRadius:8, cursor:'pointer', fontSize:12 }}>📋</button>
              <button onClick={() => { setShowMsgSearch(!showMsgSearch); setMsgSearch(''); }} style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', padding:'6px 8px', borderRadius:8, cursor:'pointer', fontSize:12 }}>🔍</button>
              <button onClick={() => setShowSaveContact(true)} style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', padding:'6px 8px', borderRadius:8, cursor:'pointer', fontSize:16 }}>👤</button>
              
            </div>
          </>
        ) : (
          <>
            <div style={{ position:'relative' }}>
              <button onClick={() => setShowBizSwitcher(v => !v)} style={{ background:'none', border:'none', padding:0, display:'flex', alignItems:'center', gap:6, cursor: availableBusinesses && availableBusinesses.length > 1 ? 'pointer' : 'default' }}>
                <span style={{ color:'#fff', fontWeight:700, fontSize:18 }}>💬 VBot</span>
                {availableBusinesses && availableBusinesses.length > 1 && (
                  <span style={{ color:'#fff', fontSize:12 }}>▾</span>
                )}
              </button>
              <div>
                <span style={{ color:'rgba(255,255,255,0.85)', fontSize:13 }}>{selectedBusiness?.name ? selectedBusiness.name + ' · ' : ''}{conversations.length} chats</span>
              </div>
              {showBizSwitcher && availableBusinesses && availableBusinesses.length > 1 && (
                <div style={{ position:'absolute', top:'110%', left:0, background:'#fff', borderRadius:12, boxShadow:'0 4px 16px rgba(0,0,0,0.2)', minWidth:200, zIndex:200, overflow:'hidden' }}>
                  <div style={{ padding:'10px 14px', fontSize:11, fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:0.5, borderBottom:'1px solid #f3f4f6' }}>📁 All businesses</div>
                  {availableBusinesses.map(b => (
                    <div key={b.id} onClick={() => { selectBusiness(b); setShowBizSwitcher(false); }}
                      style={{ padding:'12px 14px', fontSize:14, fontWeight: selectedBusiness?.id === b.id ? 700 : 400, color: selectedBusiness?.id === b.id ? '#166534' : '#111827', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #f3f4f6' }}>
                      <span>{b.name || `Business ${b.id}`}</span>
                      {selectedBusiness?.id === b.id && <span style={{ color:'#25D366' }}>✓</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display:'flex', gap:6 }}>
              <button onClick={() => setShowManageLabels(true)} style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', padding:'6px 8px', borderRadius:8, cursor:'pointer', fontSize:12 }}>🏷️</button>
              <button onClick={() => setShowManageQR(true)} style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', padding:'6px 8px', borderRadius:8, cursor:'pointer', fontSize:12 }}>⚡</button>
              <button onClick={() => setShowNewChat(true)} style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', padding:'6px 10px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700 }}>+ New</button>
            </div>
          </>
        )}
      </div>

      {/* Conversation List */}
      {!selected && (
        <div>
          <div style={{ padding:'10px 12px 4px' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts..." style={{ width:'100%', padding:'10px 14px', border:'none', borderRadius:24, fontSize:14, outline:'none', boxSizing:'border-box', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }} />
          </div>
          <div style={{ display:'flex', gap:6, padding:'8px 12px 4px', overflowX:'auto', scrollbarWidth:'none' }}>
            {filterTabs.map(f => (
              <button key={f.key} onClick={() => { setFilter(f.key); setLabelFilter(null); }}
                style={{ padding:'5px 10px', borderRadius:20, border:'none', cursor:'pointer', fontSize:12, fontWeight:700, flexShrink:0, whiteSpace:'nowrap', background: filter === f.key && !labelFilter ? '#25D366' : '#fff', color: filter === f.key && !labelFilter ? '#fff' : '#374151', boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }}>
                {f.label} <span style={{ background: filter === f.key && !labelFilter ? 'rgba(255,255,255,0.3)' : '#e5e7eb', borderRadius:10, padding:'1px 5px', fontSize:11 }}>{counts[f.key]}</span>
              </button>
            ))}
          </div>
          {labels.length > 0 && (
            <div style={{ display:'flex', gap:6, padding:'4px 12px 8px', overflowX:'auto', scrollbarWidth:'none' }}>
              {labels.map(l => { const lc = labelColors[l.color] || labelColors.green; const isActive = labelFilter?.id === l.id; const labelCount = conversations.filter(c => c.label_ids && c.label_ids.split(',').map(Number).includes(l.id)).length; return (
                <button key={l.id} onClick={() => setLabelFilter(isActive ? null : l)}
                  style={{ padding:'4px 10px', borderRadius:20, border: isActive ? '2px solid ' + lc.color : '1px solid ' + lc.bg, cursor:'pointer', fontSize:11, fontWeight:600, flexShrink:0, whiteSpace:'nowrap', background: isActive ? lc.color : lc.bg, color: isActive ? '#fff' : lc.color }}>
                  {l.name} <span style={{ background: isActive ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)', borderRadius:10, padding:'1px 5px', fontSize:10 }}>{labelCount}</span>
                </button>
              ); })}
            </div>
          )}
          {fetching ? <div style={{ textAlign:'center', padding:40, color:'#6b7280' }}>Loading...</div>
          : filtered.length === 0 ? <div style={{ textAlign:'center', padding:40, color:'#6b7280' }}>No conversations</div>
          : filtered.map((conv, i) => {
            const typeLabel = getTypeLabel(conv);
            const win = get24HrWindow(conv.has_inbound ? conv.last_message_time : null);
            return (
              <div key={i} onClick={() => openConversation(conv)} style={{ background:'#fff', padding:'11px 16px', borderBottom:'1px solid #f0f2f5', display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
                <div style={{ position:'relative', flexShrink:0 }}>
                  <div style={{ width:46, height:46, borderRadius:'50%', background:getColor(conv.name || conv.phone), display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:16 }}>{getInitials(conv.name || conv.phone)}</div>
                  {conv.unread_count > 0 && <div style={{ position:'absolute', top:-2, right:-2, background:'#25D366', color:'#fff', borderRadius:10, padding:'1px 5px', fontSize:10, fontWeight:700, minWidth:16, textAlign:'center' }}>{conv.unread_count}</div>}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
                    <span style={{ fontWeight:700, color:'#111', fontSize:14 }}>{conv.name || conv.phone}</span>
                    <span style={{ color:'#9ca3af', fontSize:11 }}>{formatTime(conv.last_message_time)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:4 }}>
                    <span style={{ color:'#6b7280', fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
                      {(() => { const m = (conv.last_message || '').split('\n\n').pop() || ''; if(m === '[document]') return '📄 Document'; if(m === '[image]') return '🖼️ Image'; if(m === '[video]') return '🎥 Video'; if(m === '[audio]') return '🎵 Audio'; return m; })()}
                    </span>
                    <div style={{ display:'flex', gap:3, flexShrink:0 }}>
                      {typeLabel && <span style={{ background:typeLabel.bg, color:typeLabel.color, borderRadius:6, padding:'1px 6px', fontSize:10, fontWeight:700 }}>{typeLabel.text}</span>}
                      {win && <span style={{ background:'#fff7ed', color:'#c2410c', borderRadius:6, padding:'1px 5px', fontSize:10 }}>⏱{win}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Chat View */}
      {selected && (
        <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 62px)' }}>
          {contactLabels.length > 0 && (
            <div style={{ background:'#fff', padding:'6px 12px', display:'flex', gap:4, flexWrap:'wrap', borderBottom:'1px solid #f0f2f5' }}>
              {contactLabels.map(l => { const lc = labelColors[l.color] || labelColors.green; return <span key={l.id} style={{ background:lc.bg, color:lc.color, borderRadius:12, padding:'2px 8px', fontSize:11, fontWeight:600 }}>{l.name}</span>; })}
            </div>
          )}

          {/* Message Search Bar */}
          {showMsgSearch && (
            <div style={{ background:'#fff', padding:'8px 12px', borderBottom:'1px solid #f0f2f5', display:'flex', gap:8, alignItems:'center' }}>
              <input value={msgSearch} onChange={e => setMsgSearch(e.target.value)}
                placeholder="Search in conversation..."
                autoFocus
                style={{ flex:1, padding:'8px 14px', border:'1px solid #e5e7eb', borderRadius:20, fontSize:13, outline:'none' }} />
              {msgSearch && <span style={{ fontSize:11, color:'#6b7280', flexShrink:0 }}>{messages.filter(m => m.message?.toLowerCase().includes(msgSearch.toLowerCase())).length} found</span>}
            </div>
          )}

          {/* Quick Reply Pills */}
          {showQuickReplies && quickReplies.length > 0 && (
            <div style={{ background:'#fff', padding:'8px 12px', borderBottom:'1px solid #f0f2f5' }}>
              <div style={{ fontSize:11, color:'#6b7280', marginBottom:6, fontWeight:600 }}>⚡ Quick Replies</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {quickReplies.map(qr => (
                  <button key={qr.id} onClick={() => applyQuickReply(qr.message)}
                    style={{ padding:'5px 12px', borderRadius:20, border:'1px solid #25D366', background:'#f0fdf4', color:'#166534', fontSize:12, cursor:'pointer', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {qr.message}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div id='chat-scroll' style={{ flex:1, overflowY:'auto', padding:'12px 10px', display:'flex', flexDirection:'column', gap:6, background:'#ece5dd' }}>
            {messages.length === 0 && <div style={{ textAlign:'center', color:'#9ca3af', marginTop:40, fontSize:14 }}>No messages</div>}
            {(msgSearch ? messages.filter(m => m.message?.toLowerCase().includes(msgSearch.toLowerCase())) : messages).map((msg, i, arr) => {
              const label = getDateLabel(msg.created_at);
              const prevLabel = i > 0 ? getDateLabel(arr[i-1].created_at) : null;
              const showDate = label && label !== prevLabel;
              return (
              <>
              {showDate && (
                <div style={{ display:'flex', justifyContent:'center', margin:'8px 0' }}>
                  <span style={{ background:'rgba(0,0,0,0.15)', color:'#fff', fontSize:11, padding:'3px 12px', borderRadius:12, fontWeight:600 }}>{label}</span>
                </div>
              )}
              <div key={i} style={{ display:'flex', justifyContent: msg.direction === 'outbound' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth:'78%', padding:'8px 12px', borderRadius: msg.direction === 'outbound' ? '12px 2px 12px 12px' : '2px 12px 12px 12px', background: msg.direction === 'outbound' ? '#dcf8c6' : '#fff', boxShadow:'0 1px 2px rgba(0,0,0,0.1)', fontSize:14, color:'#111', lineHeight:1.4 }}>
                  {msg.media_url && msg.media_type === 'image' && (
                    <a href={msg.media_url} target="_blank" rel="noreferrer" style={{ display:'block' }}>
                      <img src={msg.media_url} alt="" style={{ maxWidth:'100%', borderRadius:8, marginBottom:4, cursor:'pointer' }} />
                    </a>
                  )}
                  {msg.media_url && msg.media_type === 'video' && <video controls style={{ maxWidth:'100%', borderRadius:8, marginBottom:4 }}><source src={msg.media_url} /></video>}
                  {msg.media_url && (msg.media_type === 'audio' || msg.msg_type === 'audio' || msg.message === '[audio]') && (() => {
                    const aid = 'audio-' + i;
                    return (
                    <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:220, maxWidth:260, padding:'4px 0' }}>
                      <audio id={aid} src={msg.media_url} preload="metadata"
                        onLoadedMetadata={e => { const el = e.target; if(el.duration === Infinity || isNaN(el.duration)) { el.currentTime = 1e101; el.ontimeupdate = () => { el.ontimeupdate = null; el.currentTime = 0; const d = document.getElementById('dur-'+i); if(d) d.textContent = isNaN(el.duration) ? '0:00' : Math.floor(el.duration/60)+':'+(Math.floor(el.duration%60)+'').padStart(2,'0'); }; } else { const d = document.getElementById('dur-'+i); if(d) d.textContent = Math.floor(el.duration/60)+':'+(Math.floor(el.duration%60)+'').padStart(2,'0'); } }}
                        onTimeUpdate={e => { const p = document.getElementById('prog-'+i); if(p && e.target.duration) p.style.width = (e.target.currentTime/e.target.duration*100)+'%'; const d = document.getElementById('dur-'+i); if(d && !e.target.paused) d.textContent = Math.floor(e.target.currentTime/60)+':'+(Math.floor(e.target.currentTime%60)+'').padStart(2,'0'); }}
                        onEnded={e => { const p = document.getElementById('prog-'+i); if(p) p.style.width='0%'; const ic = document.getElementById('plic-'+i); if(ic) ic.innerHTML='<polygon points=\'6,4 18,12 6,20\' fill=\'white\'/>'; const d = document.getElementById('dur-'+i); const el = document.getElementById(aid); if(d && el) d.textContent = Math.floor(el.duration/60)+':'+(Math.floor(el.duration%60)+'').padStart(2,'0'); }} />
                      {/* Orange circle with play icon */}
                      <div onClick={() => { const a = document.getElementById(aid); const ic = document.getElementById('plic-'+i); if(a.paused) { a.play(); if(ic) ic.innerHTML='<rect x=\'6\' y=\'4\' width=\'4\' height=\'16\' fill=\'white\'/><rect x=\'14\' y=\'4\' width=\'4\' height=\'16\' fill=\'white\'/>'; } else { a.pause(); if(ic) ic.innerHTML='<polygon points=\'6,4 18,12 6,20\' fill=\'white\'/>'; } }}
                        style={{ width:46, height:46, borderRadius:'50%', background:'#FF9800', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, boxShadow:'0 2px 6px rgba(0,0,0,0.2)' }}>
                        <svg id={'plic-'+i} width="24" height="24" viewBox="0 0 24 24">
                          <polygon points="6,4 18,12 6,20" fill="white"/>
                        </svg>
                      </div>
                      {/* Waveform + progress */}
                      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
                        <div style={{ position:'relative', height:4, background:'rgba(0,0,0,0.15)', borderRadius:2, cursor:'pointer' }}
                          onClick={e => { const a = document.getElementById(aid); if(a.duration) { const rect = e.currentTarget.getBoundingClientRect(); const pct = (e.clientX - rect.left)/rect.width; a.currentTime = pct * a.duration; } }}>
                          <div id={'prog-'+i} style={{ height:'100%', background: msg.direction==='outbound' ? '#075E54' : '#919191', borderRadius:2, width:'0%', transition:'width 0.5s linear' }}/>
                          <div style={{ position:'absolute', top:'50%', left:'0%', transform:'translateY(-50%)', width:12, height:12, borderRadius:'50%', background: msg.direction==='outbound' ? '#075E54' : '#5a5a5a', marginLeft:-6 }}/>
                        </div>
                        <span id={'dur-'+i} style={{ fontSize:11, color: msg.direction==='outbound' ? '#075E54' : '#666' }}>0:00</span>
                      </div>
                    </div>
                    );
                  })()}
                  {msg.media_url && msg.media_type === 'document' && <a href={msg.media_url} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', gap:8, background:'#f0f4ff', borderRadius:8, padding:'8px 12px', marginBottom:4, textDecoration:'none' }}><span>📄</span><span style={{ fontSize:13, fontWeight:600, color:'#1a1a1a' }}>{msg.media_filename || 'Document'}</span></a>}
                  {msg.message === '[reaction]' && (
                    <div style={{ fontSize:24, padding:'4px 8px' }}>👍</div>
                  )}
                  {msg.message && msg.message.startsWith('Reacted:') && (
                    <div style={{ fontSize:24, padding:'4px 8px' }}>{msg.message.replace('Reacted:', '').trim()}</div>
                  )}
                  {msg.message && !['[image]','[video]','[audio]','[document]','[sticker]','[reaction]'].includes(msg.message) && !msg.message.startsWith('Reacted:') && (
                    <div style={{ whiteSpace:'pre-wrap', position:'relative' }}
                      onDoubleClick={() => copyMessage(msg.message)}
                      onContextMenu={e => { e.preventDefault(); setReplyTo(msg); }}>
                      {msg.message}
                      {copiedMsg === msg.message && <span style={{ position:'absolute', top:-20, right:0, background:'#333', color:'#fff', fontSize:10, padding:'2px 6px', borderRadius:4, whiteSpace:'nowrap' }}>Copied!</span>}
                    </div>
                  )}
                  <div style={{ display:'flex', justifyContent: msg.direction === 'outbound' ? 'flex-start' : 'flex-end', marginTop:2 }}>
                    <button onClick={() => setReplyTo(msg)} style={{ background:'none', border:'none', fontSize:12, color:'#9ca3af', cursor:'pointer', padding:'0 2px' }}>↩️ Reply</button>
                  </div>
                  <div style={{ fontSize:11, color:'#9ca3af', marginTop:3, textAlign:'right' }}>
                    {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }) : ''}
                    {msg.direction === 'outbound' && <span style={{ color: msg.status === 'read' ? '#34B7F1' : '#aaa', fontSize: 11 }}>✓✓</span>}
                  </div>
                </div>
              </div>
              </>
              );
            })}
            
          <div ref={chatEndRef} />
          </div>

          {/* Reply Box */}
          {replyTo && (
            <div style={{ background:'#fff', padding:'8px 12px', borderLeft:'4px solid #25D366', margin:'0 10px 4px', borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:11, color:'#25D366', fontWeight:700, marginBottom:2 }}>↩️ Replying to</div>
                <div style={{ fontSize:12, color:'#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {replyTo.media_type === 'image' ? '🖼️ Image' : replyTo.media_type === 'video' ? '🎥 Video' : replyTo.media_type === 'audio' ? '🎵 Audio' : replyTo.media_type === 'document' ? '📄 Document' : replyTo.message}
                </div>
              </div>
              <button onClick={() => setReplyTo(null)} style={{ background:'none', border:'none', fontSize:16, color:'#9ca3af', cursor:'pointer', marginLeft:8 }}>✕</button>
            </div>
          )}
          <div style={{ background:'#f0f2f5', padding:'8px 10px', paddingBottom:'env(safe-area-inset-bottom, 8px)', display:'flex', gap:8, alignItems:'center' }}>
            <input type="file" id="mob-upload" accept="image/*" multiple style={{ display:'none' }} onChange={e => { if(e.target.files[0]) uploadMedia(e.target.files[0]); e.target.value=''; setShowAttachMenu(false); }} />
            <input type="file" id="mob-upload-doc" accept="application/pdf,.doc,.docx" style={{ display:'none' }} onChange={e => { if(e.target.files[0]) uploadMedia(e.target.files[0]); e.target.value=''; setShowAttachMenu(false); }} />
            <input type="file" id="mob-upload-camera" accept="image/*" capture="environment" style={{ display:'none' }} onChange={e => { if(e.target.files[0]) uploadMedia(e.target.files[0]); e.target.value=''; setShowAttachMenu(false); }} />
            <input type="file" id="mob-upload-voice" accept="audio/*" capture="microphone" style={{ display:'none' }} onChange={e => { if(e.target.files[0]) uploadMedia(e.target.files[0]); e.target.value=''; setShowAttachMenu(false); }} />
            <button onClick={() => setShowAttachMenu(true)}
              style={{ background:'#fff', border:'none', color:'#25D366', borderRadius:'50%', width:40, height:40, fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:'0 1px 4px rgba(0,0,0,0.1)' }}>📎</button>
            <button onClick={() => setShowQuickReplies(!showQuickReplies)}
              style={{ background: showQuickReplies ? '#25D366' : '#fff', border:'none', color: showQuickReplies ? '#fff' : '#25D366', borderRadius:'50%', width:40, height:40, fontSize:16, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:'0 1px 4px rgba(0,0,0,0.1)' }}>⚡</button>
            {isRecording ? (
              <div style={{ flex:1, padding:'10px 16px', borderRadius:24, background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.08)', display:'flex', alignItems:'center', gap:8, position:'relative', overflow:'hidden' }}>
                <div style={{ width:10, height:10, borderRadius:'50%', background: cancelSlide ? '#999' : '#ff4444', flexShrink:0 }}/>
                <span style={{ color: cancelSlide ? '#999' : '#ff4444', fontSize:13, fontWeight:600 }}>
                  {String(Math.floor(recordingTime/60)).padStart(2,'0')}:{String(recordingTime%60).padStart(2,'0')} &nbsp;🔴
                </span>
                {!isLocked && <span style={{ flex:1, textAlign:'center', color:'#aaa', fontSize:13 }}>{'< ← Slide to cancel'}</span>}
                {isLocked && <span style={{ flex:1, textAlign:'center', color:'#25D366', fontSize:13, fontWeight:600 }}>🔒 Locked</span>}
              </div>
            ) : (
              <input value={reply} onChange={e => setReply(e.target.value)} placeholder="Type a message..."
                onKeyDown={e => e.key === 'Enter' && sendReply()}
                style={{ flex:1, padding:'10px 16px', border:'none', borderRadius:24, fontSize:14, outline:'none', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }} />
            )}
            {reply.trim() ? (
              <button onClick={sendReply} disabled={sending}
                style={{ background:'#25D366', border:'none', color:'#fff', borderRadius:'50%', width:44, height:44, fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>➤</button>
            ) : isRecording && isLocked ? (
              <button onClick={() => stopRecording(false)}
                style={{ background:'#25D366', border:'none', color:'#fff', borderRadius:'50%', width:44, height:44, fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>➤</button>
            ) : isRecording ? (
              <button
                onTouchEnd={onMicTouchEnd} onMouseUp={onMicTouchEnd}
                onTouchMove={onMicTouchMove} onMouseMove={onMicTouchMove}
                style={{ background:'#ff4444', border:'none', color:'#fff', borderRadius:'50%', width:44, height:44, fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M12 1c-2.21 0-4 1.79-4 4v6c0 2.21 1.79 4 4 4s4-1.79 4-4V5c0-2.21-1.79-4-4-4z"/>
                  <path d="M19 11c0 3.87-3.13 7-7 7s-7-3.13-7-7H3c0 4.42 3.28 8.07 7.5 8.72V22h3v-2.28C17.72 19.07 21 15.42 21 11h-2z"/>
                </svg>
                </button>
            ) : (
              <button
                onTouchStart={onMicTouchStart} onMouseDown={onMicTouchStart}
                onTouchEnd={onMicTouchEnd} onMouseUp={onMicTouchEnd}
                onTouchMove={onMicTouchMove} onMouseMove={onMicTouchMove}
                style={{ background:'#25D366', border:'none', color:'#fff', borderRadius:'50%', width:44, height:44, fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M12 1c-2.21 0-4 1.79-4 4v6c0 2.21 1.79 4 4 4s4-1.79 4-4V5c0-2.21-1.79-4-4-4z"/>
                  <path d="M19 11c0 3.87-3.13 7-7 7s-7-3.13-7-7H3c0 4.42 3.28 8.07 7.5 8.72V22h3v-2.28C17.72 19.07 21 15.42 21 11h-2z"/>
                </svg>
                </button>
            )}
          </div>
        </div>
      )}

      {/* Label Modal */}
      {showLabelModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'flex-end' }} onClick={() => setShowLabelModal(false)}>
          <div style={{ background:'#fff', width:'100%', borderRadius:'16px 16px 0 0', padding:20, maxHeight:'70vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:16 }}>🏷️ Assign Labels</div>
            {labels.length === 0 ? <div style={{ color:'#6b7280', textAlign:'center', padding:20 }}>No labels yet.</div>
            : labels.map(l => { const lc = labelColors[l.color] || labelColors.green; const has = contactLabels.find(cl => cl.id === l.id); return (
              <div key={l.id} onClick={() => toggleLabel(l)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', borderRadius:10, border: has ? '2px solid ' + lc.color : '1px solid #e5e7eb', marginBottom:8, cursor:'pointer', background: has ? lc.bg : '#f9fafb' }}>
                <span style={{ fontWeight:600, fontSize:14, color: has ? lc.color : '#111' }}>{l.name}</span>
                {has && <span style={{ color: lc.color, fontSize:18 }}>✓</span>}
              </div>
            ); })}
            <button onClick={() => setShowLabelModal(false)} style={{ width:'100%', padding:12, background:'#f3f4f6', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', marginTop:8 }}>Done</button>
          </div>
        </div>
      )}

      {/* Manage Labels Modal */}
      {showManageLabels && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'flex-end' }} onClick={() => setShowManageLabels(false)}>
          <div style={{ background:'#fff', width:'100%', borderRadius:'16px 16px 0 0', padding:20, maxHeight:'80vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:16 }}>🏷️ Manage Labels</div>
            {labels.map(l => { const lc = labelColors[l.color] || labelColors.green; return (
              <div key={l.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderRadius:10, border:'1px solid #e5e7eb', marginBottom:8, background:'#f9fafb' }}>
                <span style={{ background:lc.bg, color:lc.color, borderRadius:12, padding:'3px 10px', fontSize:13, fontWeight:600 }}>{l.name}</span>
                <button onClick={() => deleteLabel(l.id)} style={{ background:'#fee2e2', border:'none', color:'#991b1b', padding:'4px 10px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600 }}>Delete</button>
              </div>
            ); })}
            <div style={{ marginTop:16, borderTop:'1px solid #e5e7eb', paddingTop:16 }}>
              <div style={{ fontWeight:600, fontSize:14, marginBottom:10 }}>Create New Label</div>
              <input value={newLabelName} onChange={e => setNewLabelName(e.target.value)} placeholder="Label name..."
                style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, boxSizing:'border-box', marginBottom:10, outline:'none' }} />
              <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
                {Object.entries(labelColors).map(([key, val]) => (
                  <button key={key} onClick={() => setNewLabelColor(key)} style={{ width:32, height:32, borderRadius:'50%', background:val.color, border: newLabelColor === key ? '3px solid #111' : '3px solid transparent', cursor:'pointer' }} />
                ))}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setShowManageLabels(false)} style={{ flex:1, padding:12, background:'#f3f4f6', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer' }}>Close</button>
                <button onClick={createLabel} disabled={!newLabelName.trim()} style={{ flex:1, padding:12, background: newLabelName.trim() ? '#25D366' : '#9ca3af', color:'#fff', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer' }}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Quick Replies Modal */}
      {showManageQR && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'flex-end' }} onClick={() => setShowManageQR(false)}>
          <div style={{ background:'#fff', width:'100%', borderRadius:'16px 16px 0 0', padding:20, maxHeight:'80vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:16 }}>⚡ Quick Replies</div>
            {quickReplies.length === 0 ? <div style={{ color:'#6b7280', textAlign:'center', padding:20 }}>No quick replies yet.</div>
            : quickReplies.map(qr => (
              <div key={qr.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderRadius:10, border:'1px solid #e5e7eb', marginBottom:8, background:'#f9fafb' }}>
                <span style={{ fontSize:13, color:'#111', flex:1, marginRight:8 }}>{qr.message}</span>
                <button onClick={() => deleteQuickReply(qr.id)} style={{ background:'#fee2e2', border:'none', color:'#991b1b', padding:'4px 10px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600, flexShrink:0 }}>Delete</button>
              </div>
            ))}
            <div style={{ marginTop:16, borderTop:'1px solid #e5e7eb', paddingTop:16 }}>
              <div style={{ fontWeight:600, fontSize:14, marginBottom:10 }}>Add New Quick Reply</div>
              <textarea value={newQRMessage} onChange={e => setNewQRMessage(e.target.value)} placeholder="Type quick reply message..."
                style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, boxSizing:'border-box', marginBottom:10, outline:'none', resize:'vertical', minHeight:70 }} />
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setShowManageQR(false)} style={{ flex:1, padding:12, background:'#f3f4f6', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer' }}>Close</button>
                <button onClick={addQuickReply} disabled={!newQRMessage.trim()} style={{ flex:1, padding:12, background: newQRMessage.trim() ? '#25D366' : '#9ca3af', color:'#fff', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer' }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'flex-end' }} onClick={() => setShowTemplateModal(false)}>
          <div style={{ background:'#fff', width:'100%', borderRadius:'16px 16px 0 0', padding:20, maxHeight:'70vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:16 }}>📋 Send Template</div>
            {templates.length === 0 ? <div style={{ color:'#6b7280', textAlign:'center', padding:20 }}>No templates</div>
            : templates.map((t, i) => (
              <div key={i} onClick={() => sendTemplate(t.name)} style={{ padding:'12px 14px', borderRadius:10, border:'1px solid #e5e7eb', marginBottom:8, cursor:'pointer', background:'#f9fafb' }}>
                <div style={{ fontWeight:600, fontSize:14 }}>{t.name}</div>
                <div style={{ color:'#6b7280', fontSize:12, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.body || ''}</div>
              </div>
            ))}
            <button onClick={() => setShowTemplateModal(false)} style={{ width:'100%', padding:12, background:'#f3f4f6', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', marginTop:8 }}>Cancel</button>
          </div>
        </div>
      )}

      {/* New Chat Modal */}
      {showAttachMenu && (
  <div onClick={() => setShowAttachMenu(false)} style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'flex-end' }}>
    <div onClick={e => e.stopPropagation()} style={{ background:'#fff', width:'100%', borderRadius:'16px 16px 0 0', padding:'20px 24px 32px' }}>
      <div style={{ fontWeight:700, fontSize:16, marginBottom:20, color:'#111' }}>Share</div>
      <div style={{ display:'flex', gap:24, justifyContent:'center' }}>
        <div onClick={e => { e.stopPropagation(); document.getElementById('mob-upload').click(); }} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, cursor:'pointer' }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:'#e8f5e9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26 }}>🖼️</div>
          <span style={{ fontSize:13, color:'#333' }}>Photos</span>
        </div>
        
        <div onClick={e => { e.stopPropagation(); document.getElementById('mob-upload-camera').click(); }} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, cursor:'pointer' }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:'#e3f2fd', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26 }}>📷</div>
          <span style={{ fontSize:13, color:'#333' }}>Camera</span>
        </div>
        <div onClick={e => { e.stopPropagation(); document.getElementById('mob-upload-doc').click(); }} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, cursor:'pointer' }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:'#fce4ec', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26 }}>📄</div>
          <span style={{ fontSize:13, color:'#333' }}>Document</span>
        </div>

      </div>
    </div>
  </div>
)}
{showSaveContact && (
  <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-end' }}>
    <div style={{ background:'#fff', width:'100%', borderRadius:'16px 16px 0 0', padding:24 }}>
      <div style={{ fontWeight:600, fontSize:16, marginBottom:16 }}>Save Contact</div>
      <input value={saveContactName} onChange={e => setSaveContactName(e.target.value)} placeholder="Enter name" style={{ width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, fontSize:14, marginBottom:12, boxSizing:'border-box' }} />
      <input value={selected?.phone || ''} readOnly style={{ width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, fontSize:14, marginBottom:16, boxSizing:'border-box', background:'#f5f5f5' }} />
      <button onClick={async () => {
        if (!saveContactName.trim()) return;
        try {
          await api.post('/contacts', { phone: selected?.phone, name: saveContactName, business_id: bizId });
          setShowSaveContact(false);
          setSaveContactName('');
          alert('Contact saved!');
        } catch(e) { alert('Failed: ' + e.message); }
      }} style={{ width:'100%', background:'#25D366', color:'#fff', border:'none', padding:12, borderRadius:8, fontSize:15, fontWeight:600, cursor:'pointer', marginBottom:8 }}>Save Contact</button>
      <button onClick={() => setShowSaveContact(false)} style={{ width:'100%', background:'#f5f5f5', color:'#333', border:'none', padding:12, borderRadius:8, fontSize:15, cursor:'pointer' }}>Cancel</button>
    </div>
  </div>
)}
{showNewChat && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'flex-end' }} onClick={() => setShowNewChat(false)}>
          <div style={{ background:'#fff', width:'100%', borderRadius:'16px 16px 0 0', padding:20 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:16 }}>💬 New Chat</div>
            <input value={newChatPhone} onChange={e => setNewChatPhone(e.target.value)} placeholder="Phone number (with country code)"
              style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, boxSizing:'border-box', marginBottom:12, outline:'none' }} />
            <select value={newChatTemplate} onChange={e => { setNewChatTemplate(e.target.value); setNewChatVars({}); }}
              style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, boxSizing:'border-box', marginBottom:12, background:'#fff', outline:'none' }}>
              <option value="">Select template...</option>
              {templates.filter(t => t.is_new_chat == 1).map((t, i) => <option key={i} value={t.name}>{t.name}</option>)}
            </select>
            {newChatTemplate && (() => {
              const tpl = templates.find(t => t.name === newChatTemplate);
              const matches = tpl ? [...(tpl.body || '').matchAll(/\{\{(\d+)\}\}/g)] : [];
              return matches.map(m => (
                <input key={m[1]} placeholder={`Variable {{${m[1]}}}`}
                  value={newChatVars[m[1]] || ''}
                  onChange={e => setNewChatVars(v => ({...v, [m[1]]: e.target.value}))}
                  style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, boxSizing:'border-box', marginBottom:8, outline:'none' }} />
              ));
            })()}
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setShowNewChat(false)} style={{ flex:1, padding:12, background:'#f3f4f6', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer' }}>Cancel</button>
              <button onClick={sendNewChat} disabled={!newChatPhone.trim() || !newChatTemplate} style={{ flex:1, padding:12, background: newChatPhone.trim() && newChatTemplate ? '#25D366' : '#9ca3af', color:'#fff', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer' }}>Send</button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'flex-end' }} onClick={() => setShowNotesModal(false)}>
          <div style={{ background:'#fff', width:'100%', borderRadius:'16px 16px 0 0', padding:20, maxHeight:'80vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:16 }}>📝 Contact Notes</div>
            {notes.length === 0 ? <div style={{ color:'#6b7280', textAlign:'center', padding:20 }}>No notes yet.</div>
            : notes.map(n => (
              <div key={n.id} style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'10px 14px', borderRadius:10, border:'1px solid #e5e7eb', marginBottom:8, background:'#fffbeb' }}>
                <div style={{ flex:1, marginRight:8 }}>
                  <div style={{ fontSize:13, color:'#111' }}>{n.note}</div>
                  <div style={{ fontSize:11, color:'#9ca3af', marginTop:4 }}>{new Date(n.created_at).toLocaleDateString()}</div>
                </div>
                <button onClick={() => deleteNote(n.id)} style={{ background:'#fee2e2', border:'none', color:'#991b1b', padding:'4px 10px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600, flexShrink:0 }}>Delete</button>
              </div>
            ))}
            <div style={{ marginTop:16, borderTop:'1px solid #e5e7eb', paddingTop:16 }}>
              <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..."
                style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, boxSizing:'border-box', marginBottom:10, outline:'none', resize:'vertical', minHeight:80 }} />
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setShowNotesModal(false)} style={{ flex:1, padding:12, background:'#f3f4f6', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer' }}>Close</button>
                <button onClick={addNote} disabled={!newNote.trim()} style={{ flex:1, padding:12, background: newNote.trim() ? '#f59e0b' : '#9ca3af', color:'#fff', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer' }}>Save Note</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
